// Shared GPIO-type bitmask utilities and signal-normalization helpers, used
// by both the device-profile/device-config wizards (AddDeviceProfileWizard.jsx)
// and DeviceEditSidebar.jsx - factored out to a standalone module so those
// two files can share this logic without importing from each other.

// SIGNAL_TYPE_BITMAP (see 00117-starek-324-nwm's starek_324_nwm.h) - a port's
// `type` is a single bitflagged value assembled from three independent axes:
// Discrete/Proportional, Sense/Actuate, Control/State. The bit positions
// themselves are never hardcoded here as the source of truth - they're
// fetched from GET /_types (Device.FLAGS, backend-served so firmware/
// backend/UI can't drift apart). This is only the fallback used until that
// response has loaded.
export const FALLBACK_DEVICE_FLAGS = {
    BIT_MASK_OUTPUT: 1 << 0,
    BIT_MASK_INPUT: 1 << 1,
    BIT_MASK_DIGITAL: 1 << 2,
    BIT_MASK_ANALOG: 1 << 3,
    BIT_MASK_STATUS: 1 << 4,
    BIT_MASK_CONTROL: 1 << 5,
    BUS_UART: 64,
    UART_BAUD_SHIFT: 8,
    UART_BAUD_MASK: 0x700,
    UART_DATA_BITS_SHIFT: 11,
    UART_DATA_BITS_MASK: 0x1800,
    UART_PARITY_SHIFT: 13,
    UART_PARITY_MASK: 0x6000,
    UART_STOP_BITS_SHIFT: 15,
    UART_STOP_BITS_MASK: 0x8000,
};

// Composes a GPIO port's numeric type from its three dropdown values. Some
// models (e.g. starek-nwm-sim) fix each GPIO port to exactly one role at the
// manifest level instead of exposing the three configurable dropdowns -
// there's nothing to derive type from, so the manifest's own fixed value
// (seeded onto the channel at init) is authoritative and must survive
// untouched.
export function computeGpioType(channel, flags) {
    const hasConfigurableRole =
        channel.discreteOrProportional !== undefined ||
        channel.actuateOrSense !== undefined ||
        channel.controlOrState !== undefined;
    if (!hasConfigurableRole) return channel.type;

    let type = 0;
    if (channel.discreteOrProportional === "Discrete") type |= flags.BIT_MASK_DIGITAL;
    else if (channel.discreteOrProportional === "Proportional") type |= flags.BIT_MASK_ANALOG;
    if (channel.actuateOrSense === "Sense") type |= flags.BIT_MASK_INPUT;
    else if (channel.actuateOrSense === "Actuate") type |= flags.BIT_MASK_OUTPUT;
    if (channel.controlOrState === "Control") type |= flags.BIT_MASK_CONTROL;
    else if (channel.controlOrState === "State") type |= flags.BIT_MASK_STATUS;
    return type;
}

// A raw manifest entry for a GPIO port doesn't carry an explicit `type` -
// only a `parameters` list of axis dropdowns (discreteOrProportional/
// actuateOrSense/controlOrState), each with a `default`. Without this, any
// GPIO port whose type has never been explicitly overridden/reported (e.g.
// port1-3 on a freshly-provisioned device that's only ever reported port0)
// reads as type: null everywhere downstream - no badges, no control widget,
// nothing - even though the manifest fully specifies what it defaults to.
// Same rationale applies to UART's baudrate/data_bits/parity/stop_bits
// parameters (see computeUartType/decodeUartType below) - a manifest entry
// only ever declares one set of parameter names, so trying both computers
// and taking whichever one actually matched is unambiguous.
export function manifestDefaultType(entry, flags) {
    if (entry?.type !== undefined && entry?.type !== null) return entry.type;
    const params = entry?.parameters;
    if (!Array.isArray(params) || params.length === 0) return undefined;
    const defaults = {};
    params.forEach((p) => {
        if (p?.name) defaults[p.name] = p.default;
    });
    const computed = computeGpioType(defaults, flags);
    if (computed !== undefined && computed !== null) return computed;
    const computedUart = computeUartType(defaults, params, flags);
    return computedUart === undefined || computedUart === null ? undefined : computedUart;
}

// Inverse of computeGpioType - decodes a bitmask back into the three
// dropdown values, only when the manifest entry actually declares them
// (fixed-type models like starek-nwm-sim don't, and have nothing to decode
// into).
export function decodeGpioType(type, flags, manifestParams) {
    const paramNames = new Set((manifestParams || []).map((p) => p.name));
    if (type === undefined || type === null || paramNames.size === 0) return {};
    const decoded = {};
    if (paramNames.has("discreteOrProportional")) {
        decoded.discreteOrProportional = type & flags.BIT_MASK_ANALOG
            ? "Proportional"
            : type & flags.BIT_MASK_DIGITAL
              ? "Discrete"
              : undefined;
    }
    if (paramNames.has("actuateOrSense")) {
        decoded.actuateOrSense = type & flags.BIT_MASK_OUTPUT
            ? "Actuate"
            : type & flags.BIT_MASK_INPUT
              ? "Sense"
              : undefined;
    }
    if (paramNames.has("controlOrState")) {
        decoded.controlOrState = type & flags.BIT_MASK_STATUS
            ? "State"
            : type & flags.BIT_MASK_CONTROL
              ? "Control"
              : undefined;
    }
    return decoded;
}

// UART peripheral-init parameters, packed into type bits D08-D15. The bit
// LAYOUT (which shift/mask means which field) is a firmware wire-protocol
// contract, backend-served via GET /_types (Device.FLAGS.UART_*_SHIFT/MASK,
// see Device.ts) exactly like the D00-D07 axis bits above - `flags` here is
// that same object, never a local hardcoded copy.
//
// The VALUES each field can take (which baud rates/parities exist, and
// their index order) are NOT protocol - they're per-device-model data, and
// come entirely from `manifestParams` (each param's own `range` array, as
// declared in the device model's manifest - starek_configurations table,
// device.available_models - the exact same data the Device Profile wizard
// already renders its dropdown options from). An index is just this
// field's position in that array; there is no separate value table to keep
// in sync anywhere in the UI.
const UART_PARAM_NAMES = ["baudrate", "data_bits", "parity", "stop_bits"];

function uartParamsByName(manifestParams) {
    const byName = {};
    (manifestParams || []).forEach((p) => {
        if (p?.name) byName[p.name] = p;
    });
    return byName;
}

// Composes a UART port's numeric type from its 4 dropdown values - mirrors
// computeGpioType's "hasConfigurableRole" early-return for buses that don't
// declare these params (e.g. a manifest without a /uart entry at all).
export function computeUartType(channel, manifestParams, flags) {
    const paramByName = uartParamsByName(manifestParams);
    const hasConfigurableRole = UART_PARAM_NAMES.some(
        (name) => channel[name] !== undefined && paramByName[name],
    );
    if (!hasConfigurableRole) return channel.type;

    let type = flags.BUS_UART;
    const pack = (name, shift) => {
        const range = paramByName[name]?.range || [];
        const idx = range.indexOf(String(channel[name]));
        if (idx >= 0) type |= idx << shift;
    };
    pack("baudrate", flags.UART_BAUD_SHIFT);
    pack("data_bits", flags.UART_DATA_BITS_SHIFT);
    pack("parity", flags.UART_PARITY_SHIFT);
    pack("stop_bits", flags.UART_STOP_BITS_SHIFT);
    return type;
}

// Inverse of computeUartType - only decodes fields the manifest actually
// declares, same paramNames-gating pattern as decodeGpioType. Each value
// comes straight out of the manifest's own `range` array at the decoded
// index - if a model's manifest ever has fewer options than an index
// implies, this correctly yields undefined rather than a stale/invented
// value.
export function decodeUartType(type, manifestParams, flags) {
    const paramByName = uartParamsByName(manifestParams);
    if (type === undefined || type === null || Object.keys(paramByName).length === 0) return {};
    const decoded = {};
    const unpack = (name, shift, mask) => {
        if (!paramByName[name]) return;
        decoded[name] = paramByName[name].range?.[(type & mask) >> shift];
    };
    unpack("baudrate", flags.UART_BAUD_SHIFT, flags.UART_BAUD_MASK);
    unpack("data_bits", flags.UART_DATA_BITS_SHIFT, flags.UART_DATA_BITS_MASK);
    unpack("parity", flags.UART_PARITY_SHIFT, flags.UART_PARITY_MASK);
    unpack("stop_bits", flags.UART_STOP_BITS_SHIFT, flags.UART_STOP_BITS_MASK);
    return decoded;
}

// overrides.signals is an array of {name, type} for any device that's ever
// gone through assignDeviceProfile/updateDeviceSignalType - but a device
// that predates both (raw legacy data) can still have it stored as an
// object keyed by name instead. Normalize both shapes rather than assuming
// array, or a legacy device crashes the whole sidebar.
export function normalizeRuntimeSignals(signals) {
    if (Array.isArray(signals)) return signals;
    if (signals && typeof signals === "object") {
        return Object.entries(signals).map(([name, val]) => ({
            name,
            type: val?.type,
        }));
    }
    return [];
}

// ShadowDevice.parameters is a MySQL JSON column and Device.overrides/
// reported.signals are DynamoDB Lists merged server-side - none of these
// guarantee a stable key order, so anywhere signals get listed (the
// sidebar's Signals tab, LiveView's channel switcher) needs the same
// explicit deterministic sort or the two views drift out of sync with each
// other on every reload. Sort by bus (gpio/i2c/uart/virt), then by any
// trailing port number, then alphabetically as a last resort.
const SIGNAL_BUS_ORDER = ["/gpio/", "/i2c", "/uart", "/virt/"];
function signalSortKey(key) {
    const busIndex = SIGNAL_BUS_ORDER.findIndex((prefix) => key.startsWith(prefix));
    const portMatch = key.match(/(\d+)$/);
    const portIndex = portMatch ? Number(portMatch[1]) : 0;
    return [busIndex === -1 ? SIGNAL_BUS_ORDER.length : busIndex, portIndex, key];
}
export function compareSignalKeys(a, b) {
    const [busA, portA, keyA] = signalSortKey(a);
    const [busB, portB, keyB] = signalSortKey(b);
    if (busA !== busB) return busA - busB;
    if (portA !== portB) return portA - portB;
    return keyA.localeCompare(keyB);
}

// Physical signals carry no stored color of their own (cosmetics are an
// Interpretation-only concept now) - their displayed color is always this
// computed BBROYGBVGW sequence instead, assigned by compareSignalKeys
// order so an address gets the same color everywhere it's shown (LiveView's
// graph, and the Source dropdown's color dot in the sidebar/wizard
// interpretation forms). An interpretation inherits its `source` signal's
// entry from this same map as its own default color.
export function computePhysicalSignalColors(physicalKeys, palette) {
    const sorted = [...physicalKeys].sort(compareSignalKeys);
    const colors = {};
    sorted.forEach((key, idx) => {
        colors[key] = palette[idx % palette.length];
    });
    return colors;
}
